import type { DragEvent } from 'react';
import { allTemplates } from '../lib/templates';
import type { Workflow } from '../types';
import { Icon, type IconName } from './Icon';

interface PaletteItem {
  kind: 'trigger' | 'wait' | 'condition' | 'action' | 'ai_smart';
  icon: IconName;
  title: string;
  desc: string;
  cls: 'trig' | 'wait' | 'cond' | 'act' | 'ai';
}

const ITEMS: PaletteItem[] = [
  { kind: 'trigger',   icon: 'lightning', title: 'When this happens', desc: 'Start the workflow',          cls: 'trig' },
  { kind: 'wait',      icon: 'clock',     title: 'Wait a bit',         desc: 'Pause before next step',     cls: 'wait' },
  { kind: 'condition', icon: 'diamond',   title: 'Only if',             desc: 'If / else branch',          cls: 'cond' },
  { kind: 'action',    icon: 'play',      title: 'Then do this',        desc: 'SMS, email, task, webhook', cls: 'act' },
  { kind: 'ai_smart',  icon: 'sparkle',   title: 'Ask AI',              desc: 'Custom prompt + model',     cls: 'ai' },
];

interface Props {
  onAddTemplate: (templateFn: () => Workflow) => void;
}

export function Palette({ onAddTemplate }: Props) {
  function handleDragStart(e: DragEvent<HTMLDivElement>, kind: PaletteItem['kind']) {
    e.dataTransfer.setData('application/iiq-node', kind);
    e.dataTransfer.effectAllowed = 'move';
  }

  return (
    <aside className="palette" data-tour="palette">
      <div className="palette-section">
        <h4><Icon name="grid" size={11} variant="silver" /> Drag to canvas</h4>
        {ITEMS.map(item => (
          <div
            key={item.kind}
            className={`palette-item ${item.cls}`}
            draggable
            onDragStart={e => handleDragStart(e, item.kind)}
          >
            <span className="ic"><Icon name={item.icon} size={18} variant={item.kind === 'ai_smart' ? 'gold' : 'silver'} /></span>
            <div>
              <b>{item.title}</b>
              <span>{item.desc}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="palette-section" data-tour="templates">
        <h4><Icon name="star" size={11} variant="gold" /> Starter templates</h4>
        {allTemplates.map((tplFn, idx) => {
          const sample = tplFn();
          return (
            <div
              key={idx}
              className="template-row"
              onClick={() => onAddTemplate(tplFn)}
              title="Load this workflow into the canvas"
            >
              <b><Icon name="star" size={10} variant="gold" /> {sample.name}</b>
              <span>{sample.description}</span>
            </div>
          );
        })}
      </div>

      <div className="palette-section">
        <h4><Icon name="help" size={11} variant="silver" /> Tips</h4>
        <div className="tip-card">
          <b>Connect blocks:</b> drag from the bottom dot of one block to the top dot of the next. Condition blocks have two outputs (match / no-match).
        </div>
        <div className="tip-card">
          <b>Edit:</b> click any block to open its properties on the right. Press <code>Delete</code> to remove. <b>Right-click</b> the canvas for the quick-add menu.
        </div>
      </div>
    </aside>
  );
}
